import { computed, ref, shallowRef, toValue, watch, onScopeDispose } from "vue";
import { equalFinderData } from "#ce/components/SpFinder/engine/finder-data";
import { addDays, format, isValid, parseISO } from "date-fns";
import { createApiClient } from "#/shared/api/client";

/** The `day-states` a calendar day takes from its slot count. */
const INDICATORS = Object.freeze({ NONE: "none", LOW: "low", HIGH: "high" });

/** Slot counts below this render as `low`. */
const INDICATOR_THRESHOLD = 6;

const DAY_FORMAT = "yyyy-MM-dd";

/**
 * The `sp-date-picker` day state for a slot count: `0` → `none`, `1..5` → `low`, `6+` → `high`.
 *
 * @param {Number} slots
 * @returns {String}
 */
export function indicatorFor(slots) {
  if (slots < 1) {
    return INDICATORS.NONE;
  }

  return slots < INDICATOR_THRESHOLD ? INDICATORS.LOW : INDICATORS.HIGH;
}

/**
 * Maps the endpoint's `{ "<date>": <slots> }` onto the picker's `{ "<yyyy-MM-dd>": <state> }`.
 *
 * @param {Object<string, number>} availability
 * @returns {Object<string, string>}
 */
export function toDayStates(availability) {
  const states = {};

  for (const [day, slots] of Object.entries(availability ?? {})) {
    const date = parseISO(day);

    if (isValid(date)) {
      states[format(date, DAY_FORMAT)] = indicatorFor(slots);
    }
  }

  return states;
}

/**
 * Day-availability states for the specific-day calendar, fetched for the range the calendar shows.
 *
 * @param {Object} options
 * @param {import("vue").MaybeRefOrGetter<String|undefined>} options.url - The availability
 *   endpoint. Undefined disables the indicators.
 * @param {import("vue").MaybeRefOrGetter<Object>} options.params - The filter's own submitted
 *   params (`availability`, `available_times`, `visit_types`). They override the search's copy.
 * @param {import("vue").MaybeRefOrGetter<Object|undefined>} options.search - The active
 *   provider search the states reflect. Undefined disables requests and clears day states.
 *   Its `available_date` is omitted so the request covers every visible day.
 * @param {Object} [options.api] - Data source; defaults to a client over `url`.
 */
export function useAvailabilityIndicators(options) {
  const dayStates = shallowRef({});
  const pending = ref(0);
  const loading = computed(() => pending.value > 0);

  let range = null;
  let latestRequest = 0;
  let applied;

  const enabled = computed(() => Boolean(toValue(options.url)));

  function client() {
    return options.api ?? createApiClient({ baseURL: toValue(options.url), parse: false });
  }

  function requestParams() {
    const end = parseISO(range.end);
    const search = { ...toValue(options.search) };
    delete search.available_date;

    return {
      ...search,
      ...toValue(options.params),
      after_date: range.start,
      before_date: isValid(end) ? format(addDays(end, 1), DAY_FORMAT) : range.end,
    };
  }

  /** Fetches the states for the current range. A stale response is dropped. */
  async function refresh() {
    if (!enabled.value || !range || toValue(options.search) === undefined) {
      return;
    }

    const params = requestParams();

    if (equalFinderData(params, applied)) {
      return;
    }

    applied = params;

    const request = (latestRequest += 1);

    pending.value += 1;

    try {
      const availability = await client().get("", params);

      if (request === latestRequest) {
        dayStates.value = toDayStates(availability);
      }
    } catch {
      if (request === latestRequest) {
        applied = undefined;
      }
    } finally {
      pending.value -= 1;
    }
  }

  /**
   * Adopts the range the calendar renders and fetches its states.
   *
   * @param {{ start: String, end: String }} visibleRange - `yyyy-MM-dd` bounds, inclusive.
   */
  function setVisibleRange(visibleRange) {
    range = visibleRange;

    return refresh();
  }

  function handleSearch() {
    if (toValue(options.search) === undefined) {
      latestRequest += 1;
      applied = undefined;
      dayStates.value = {};

      return;
    }

    refresh();
  }

  function dispose() {
    latestRequest += 1;
  }

  watch(() => toValue(options.search), handleSearch);
  onScopeDispose(dispose);

  return { dayStates, loading, enabled, setVisibleRange, refresh };
}
